import Bacon from 'baconjs';
import * as ramda from 'ramda';

import {serializeFilters, serializeFields} from 'nutshell-core';

import {Routing} from 'nutshell-core/routing';

import audienceFactory from './audience';
import fieldsFactory from './fields';
import filterFactory from './filter';
import listFactory from './list';
import reportParamsFactory from './report-params';
import searchFactory from './search';
import sortFactory from './sort';
import viewFactory from './view';

/**
 * Takes the existing components of the query and serializes them out to a url
 * encoded query string. Important note that it does not handle the
 * serialization of the actual filter params.
 * @see  '../serialize.js'
 *
 * @param  {Object} query - query to be serialized
 *
 * @return {string} - url encoded query string
 */
export function serialize(query) {
    const {
        search,
        fields,
        sort,
        list,
        report,
        filter: {selected: filters},
        audience,
    } = query;

    const serializedFilter = serializeFilters(filters);
    const serializedFields = serializeFields(fields.selected);

    const serializedQuery = {
        q: search.selected,
        list: list.selected,
        audience: audience.selected,
        fields: serializedFields,
        sort: sort.selected,
        filter: serializedFilter,
    };
    if (report) {
        serializedQuery.report = report.selected;
    }

    // Remove sort and q if they are undefined, because jQuery param() serializes it as an empty value
    if (!serializedQuery.q) delete serializedQuery.q;
    if (!serializedQuery.sort) delete serializedQuery.sort;
    if (!serializedQuery.list) delete serializedQuery.list;
    if (!serializedQuery.report) delete serializedQuery.report;
    if (!serializedQuery.audience) delete serializedQuery.audience;

    return Routing.param(serializedQuery);
}

/**
 * Allows us to differentiate between changes that will affect the results of
 * a query and those that won't. Specifically in this case we are trying to
 * avoid changes in the order of fields from retrieving a whole new dataset.
 *
 * This could, and probably should be more intelligent later.
 * @param {string} entityType - the entityType for which a checksum is being
 *                            generated
 *
 * @return {Function} - a function that will taken in the current query and
 *                    generate a checksum representing a unique dataset
 */
function extractDataQuery(entityType) {
    return function (query) {
        const {
            search: {selected: search},
            fields: {
                selected: {[entityType]: fields},
            },
            filter: {selected: filter},
            sort: {selected: sort},
            view: {selected: view},
        } = query;

        return {
            search,
            fields: ramda.sortBy((item) => item, fields),
            filter,
            sort,
            view,
        };
    };
}

/**
 * Factory function for Property that encodes the query being used for a
 * particular entities list.
 *
 * @param {Dispatcher} dispatcher - Single source of truth for pushing data/events
 * @param {Object} options - Immediately destructured
 * @param {string} options.topLevelEntity - One of 'accounts', 'contacts, 'leads'
 * @param {string[]} defaultFields - List of default fields to use for this
 *                                 entity
 *
 * @return {Bacon.Property} - A stream whose values are the current query being
 *                          used to search for list items, a checksum to
 *                          determine dataset uniqueness, and the encoded
 *                          querystring]
 */
export default function (dispatcher, {topLevelEntity, defaultFields}) {
    const {plug, push, stream} = dispatcher;

    // When a saved filter is loaded, grab the value and deparam it, then push
    // that out to update the query component
    plug(
        'QUERY-LOAD-SAVED',
        stream('SAVED-FILTERS-SELECT')
            .filter((savedFilter) => savedFilter) // Make sure we have a saved filter
            .map((filter) => Routing.deparam(filter.value))
    );

    const dataProps = {
        search: searchFactory(dispatcher),
        fields: fieldsFactory(dispatcher, {defaultFields, topLevelEntity}),
        filter: filterFactory(dispatcher),
        sort: sortFactory(dispatcher, topLevelEntity),
        view: viewFactory(dispatcher, topLevelEntity),
        list: listFactory(dispatcher),
        audience: audienceFactory(dispatcher),
    };

    if (topLevelEntity === 'leads') {
        dataProps.report = reportParamsFactory(dispatcher);
    }
    const queryDataProp = Bacon.combineTemplate(dataProps);

    const checksum = Bacon.update(
        undefined,
        [queryDataProp.changes().map(extractDataQuery(topLevelEntity)).map(JSON.stringify)],
        (unused, newChecksum) => newChecksum
    );

    const queryString = queryDataProp.map(serialize);

    return Bacon.combineTemplate({
        ...dataProps,
        queryString,
        checksum,

        reset() {
            push('QUERY-RESET-TO-DEFAULT');
        },
    });
}
